import React, { useCallback, useEffect, useMemo, useState } from "react";
import menu from "./MenuData";
import { NavLink, Link, useLocation } from "react-router-dom";
import Icon from "../../components/icon/Icon";
import classNames from "classnames";
import { getPublicUrl } from "../../utils/Utils";
import http from "../../utils/http-common";
import { isAbortError } from "../../utils/asyncRequestGuards";
import { hasPermission } from "../../utils/adminPermissions";

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function filterMenuByPermission(items, user) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (item.requirePermission && !hasPermission(user, item.requirePermission)) {
        return null;
      }
      const next = { ...item };
      if (Array.isArray(item.subMenu)) {
        next.subMenu = filterMenuByPermission(item.subMenu, user);
        if (item.section && next.subMenu.length === 0) return null;
      }
      return next;
    })
    .filter(Boolean);
}

/** Match sidebar highlight to router location (pathname + search when menu link includes ?). */
function menuLinkIsCurrent(link, location) {
  if (!link || link === "#") return false;
  const raw = String(link).trim();
  const pathPart = raw.startsWith("/") ? raw.slice(1) : raw;
  const target = getPublicUrl(pathPart);
  if (target.includes("?")) {
    const [tPath, tSearch = ""] = target.split("?");
    if (location.pathname !== tPath) return false;
    const want = new URLSearchParams(tSearch);
    const have = new URLSearchParams(location.search || "");
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  }
  return location.pathname === target || location.pathname.startsWith(`${target}/`);
}

function itemOrDescendantCurrent(item, location) {
  if (menuLinkIsCurrent(item.link, location)) return true;
  const kids = item.subMenu || item.sub;
  if (!Array.isArray(kids)) return false;
  return kids.some((c) => itemOrDescendantCurrent(c, location));
}

const COUNT_TTL_MS = 3 * 60 * 1000;
const COUNT_CACHE_KEY = "adminSidebarCounts";

function readCachedCounts() {
  try {
    const raw = sessionStorage.getItem(COUNT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > COUNT_TTL_MS) return null;
    return parsed.counts || null;
  } catch {
    return null;
  }
}

function writeCachedCounts(counts) {
  try {
    sessionStorage.setItem(COUNT_CACHE_KEY, JSON.stringify({ at: Date.now(), counts }));
  } catch {
    /* ignore */
  }
}

async function fetchSidebarCounts(signal) {
  const cached = readCachedCounts();
  if (cached) return cached;

  const counts = { practitioners: 0, soAssign: 0, quotes: 0 };
  const [pa, so, quotes] = await Promise.allSettled([
    http.get("/api/v1/admin/practitioner-approvals", {
      params: { hideTest: "1" },
      signal,
      suppressInlineErrorSwal: true,
    }),
    http.get("/api/v1/admin/second-opinion/cases", {
      params: { status: "to_assign" },
      signal,
      suppressInlineErrorSwal: true,
    }),
    http.get("/api/v1/quote", { signal, suppressInlineErrorSwal: true }),
  ]);

  if (pa.status === "fulfilled") {
    const meta = pa.value?.data?.meta;
    counts.practitioners = Number(meta?.awaitingVerifyCount) || 0;
  }
  if (so.status === "fulfilled") {
    const rows = Array.isArray(so.value?.data?.data) ? so.value.data.data : [];
    counts.soAssign = rows.length;
  }
  if (quotes.status === "fulfilled") {
    const rows = Array.isArray(quotes.value?.data?.data)
      ? quotes.value.data.data
      : Array.isArray(quotes.value?.data)
        ? quotes.value.data
        : [];
    counts.quotes = rows.filter((q) => {
      const s = String(q?.status || "").toLowerCase();
      return !s || s === "pending" || s === "new" || s === "open";
    }).length;
    if (!counts.quotes && rows.length) counts.quotes = rows.length;
  }

  writeCachedCounts(counts);
  return counts;
}

function CountBadge({ n }) {
  if (!n || n < 1) return null;
  return (
    <span
      className="badge badge-dim bg-primary"
      style={{ fontSize: 10, verticalAlign: "middle", flexShrink: 0 }}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

const MenuItem = ({
  icon,
  link,
  text,
  sub,
  newTab,
  mobileView,
  sidebarToggle,
  counts,
  section,
  sectionId,
  muted,
  countKey,
  open,
  onOpenSection,
}) => {
  const location = useLocation();
  const hasSub = Array.isArray(sub) && sub.length > 0;
  const isCurrent = menuLinkIsCurrent(link, location);
  const [nestedOpen, setNestedOpen] = useState(false);

  useEffect(() => {
    if (!hasSub || section) return;
    if (sub.some((c) => itemOrDescendantCurrent(c, location))) {
      setNestedOpen(true);
    }
  }, [hasSub, section, sub, location.pathname, location.search]);

  const closeMobile = (e) => {
    if (!hasSub && !newTab && mobileView) sidebarToggle(e);
  };

  const onToggle = (e) => {
    e.preventDefault();
    if (section && sectionId && onOpenSection) {
      onOpenSection(open ? null : sectionId);
      return;
    }
    setNestedOpen((v) => !v);
  };

  const isOpen = section ? Boolean(open) : nestedOpen;

  const wrapRef = useCallback(
    (node) => {
      if (!node || !hasSub) return;
      if (isOpen) {
        node.style.height = "auto";
        const subEl = node.querySelector(".nk-menu-sub");
        if (subEl) {
          node.style.height = `${subEl.scrollHeight}px`;
        }
        let parent = node.parentElement;
        while (parent) {
          const wrap = parent.closest?.(".nk-menu-wrap");
          if (!wrap || wrap === node) break;
          wrap.style.height = "auto";
          const nested = wrap.querySelector(":scope > .nk-menu-sub");
          if (nested) wrap.style.height = `${nested.scrollHeight}px`;
          parent = wrap.parentElement;
        }
      } else {
        node.style.height = "0px";
      }
    },
    [isOpen, hasSub, sub, counts]
  );

  const menuItemClass = classNames({
    "nk-menu-item": true,
    "has-sub": hasSub,
    active: isOpen && hasSub,
    "current-page": isCurrent && !hasSub,
    "admin-menu-muted": muted,
    "admin-menu-section": Boolean(section),
  });

  const label = (
    <>
      {icon && !section ? (
        <span className="nk-menu-icon">
          <Icon name={icon} />
        </span>
      ) : null}
      <span className="nk-menu-text">
        {text}
        {countKey ? <CountBadge n={counts?.[countKey]} /> : null}
      </span>
      {section ? <em className="icon ni ni-chevron-down admin-menu-car" /> : null}
    </>
  );

  return (
    <li className={menuItemClass} onClick={closeMobile}>
      {hasSub || section ? (
        <a
          href="#toggle"
          className={classNames("nk-menu-link nk-menu-toggle", {
            "admin-menu-section-toggle": section,
          })}
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          {label}
        </a>
      ) : newTab ? (
        <Link
          to={getPublicUrl(link)}
          target="_blank"
          rel="noopener noreferrer"
          className="nk-menu-link"
        >
          {label}
        </Link>
      ) : (
        <NavLink to={getPublicUrl(link || "")} className="nk-menu-link">
          {label}
        </NavLink>
      )}
      {hasSub ? (
        <div className="nk-menu-wrap" ref={wrapRef} style={{ overflow: "hidden" }}>
          <ul className="nk-menu-sub">
            {sub.map((item) => (
              <MenuItem
                key={`${item.text}-${item.link}`}
                link={item.link}
                icon={item.icon}
                text={item.text}
                sub={item.subMenu}
                newTab={item.newTab}
                sidebarToggle={sidebarToggle}
                mobileView={mobileView}
                counts={counts}
                countKey={item.countKey}
                section={false}
                muted={false}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
};

const Menu = ({ sidebarToggle, mobileView }) => {
  const location = useLocation();
  const [counts, setCounts] = useState(() => readCachedCounts() || {});

  const items = useMemo(() => {
    const user = readStoredUser();
    return filterMenuByPermission(
      menu.filter((item) => item.text !== "Applications"),
      user
    );
  }, [location.pathname]);

  const sectionForRoute = useMemo(() => {
    for (const item of items) {
      if (item.section && item.sectionId && itemOrDescendantCurrent(item, location)) {
        return item.sectionId;
      }
    }
    return null;
  }, [items, location.pathname, location.search]);

  const [openSectionId, setOpenSectionId] = useState(() => {
    const def = items.find((i) => i.section && i.defaultOpen);
    return def?.sectionId || null;
  });

  useEffect(() => {
    if (sectionForRoute) setOpenSectionId(sectionForRoute);
  }, [sectionForRoute]);

  useEffect(() => {
    const ac = new AbortController();
    fetchSidebarCounts(ac.signal)
      .then((c) => {
        if (!ac.signal.aborted) setCounts(c);
      })
      .catch((err) => {
        if (!isAbortError(err)) {
          /* keep cache / zeros */
        }
      });
    return () => ac.abort();
  }, []);

  return (
    <ul className="nk-menu nk-menu-md admin-menu-accordion">
      {items.map((item) => {
        if (item.heading) {
          return (
            <li className="nk-menu-heading" key={item.heading}>
              <h6 className="overline-title text-primary-alt">{item.heading}</h6>
            </li>
          );
        }
        if (item.section) {
          return (
            <MenuItem
              key={item.sectionId || item.text}
              link={item.link}
              icon={item.icon}
              text={item.text}
              sub={item.subMenu}
              sidebarToggle={sidebarToggle}
              mobileView={mobileView}
              counts={counts}
              section
              sectionId={item.sectionId}
              muted={item.muted}
              open={openSectionId === item.sectionId}
              onOpenSection={setOpenSectionId}
            />
          );
        }
        return (
          <MenuItem
            key={item.text}
            link={item.link}
            icon={item.icon}
            text={item.text}
            sub={item.subMenu}
            sidebarToggle={sidebarToggle}
            mobileView={mobileView}
            counts={counts}
            section={false}
            muted={false}
          />
        );
      })}
    </ul>
  );
};

export default Menu;
